/**
 * Workspace · Voucher detail (S33-child).
 *
 * A single petty-cash / cash-movement row's read-only detail page. Pulls
 * `cashMovements.get` for the row + joined account names + reversed-by
 * pointer, then renders a header + Document card + Linked-records strip
 * + audit footer. The "Void this voucher" action posts the
 * `cashMovements.reverse` mutation (which creates an append-only
 * reversal row per ADR-0012 §3).
 *
 * No standalone prototype HTML — the detail surface specs live in
 * `prototype/cash/petty-cash.html` (the activity-row component) and
 * `ADR-0012 §2/§3` (the append-only reversal model).
 *
 * @see prototype/cash/petty-cash.html
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §2 (cash-movement shape)
 */
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ArrowLeft, Printer, RotateCcw, Wallet } from "lucide-react";
import { Alert, Button, Card, CardBody } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { openDocPdf } from "../../../../lib/pdf.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import {
  CATEGORY_META,
  VoidMovementDialog,
  type PettyCashCategory,
} from "../../../../features/petty-cash/index.ts";
import { paiseToInr, formatDate } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/cash/petty-cash/$voucherId")({
  params: {
    parse: (raw) => ({ voucherId: z.string().uuid().parse(raw.voucherId) }),
    stringify: (parsed) => ({ voucherId: parsed.voucherId }),
  },
  component: VoucherDetailRoute,
});

function VoucherDetailRoute() {
  const { voucherId } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const movementQ = trpc.cashMovements.get.useQuery({ movementId: voucherId });

  const [voidOpen, setVoidOpen] = useState(false);

  const reverseMut = trpc.cashMovements.reverse.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cashMovements.get.invalidate({ movementId: voucherId }),
        utils.cashMovements.list.invalidate(),
        utils.pettyCash.anchorData.invalidate(),
        utils.briefings.workspaceSignals.invalidate(),
        utils.bankAccounts.list.invalidate(),
      ]);
      setVoidOpen(false);
      void navigate({ to: "/cash/petty-cash" });
    },
  });

  if (movementQ.isLoading) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Voucher" },
        ]}
      >
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading voucher…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }

  if (movementQ.error) {
    const isNotFound = movementQ.error.data?.code === "NOT_FOUND";
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Voucher" },
        ]}
      >
        <Alert
          intent="bad"
          title={isNotFound ? "Voucher not found." : "Couldn't load voucher."}
          description={movementQ.error.message}
        />
      </WorkspaceShell>
    );
  }

  const m = movementQ.data?.movement;
  if (!m) return null;

  const isReversed = m.reversedByMovementId !== null || m.reversesMovementId !== null;
  const category = (m.expenseCategory ?? "sundries") as PettyCashCategory;
  const catMeta = CATEGORY_META[category];

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Petty cash", to: "/cash/petty-cash" },
        { label: "Voucher" },
      ]}
    >
      <div className="flex flex-col gap-5 max-w-3xl">
        {/* Header */}
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Link
              to="/cash/petty-cash"
              className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline shrink-0"
              aria-label="Back to petty cash"
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <KindPill kind={m.kind} />
                {isReversed ? (
                  <span className="inline-flex items-center px-1.5 h-5 rounded-full border border-bad-100 bg-bad-50 text-bad text-xs font-semibold">
                    Reversed
                  </span>
                ) : null}
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight truncate">
                {m.narration}
              </h1>
              <p className="mt-1 text-xs text-muted">
                {formatDate(m.valueDate)} · Posted by {m.createdByActorId || "—"}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <button
              type="button"
              onClick={() => openDocPdf(`/api/cash-movements/${m.id}/pdf`)}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm border border-line bg-paper text-ink text-xs font-semibold hover:bg-bg"
            >
              <Printer size={13} strokeWidth={2} />
              Print
            </button>
            <p className="text-3xl font-bold tracking-tight num leading-none text-bad">
              −{paiseToInr(m.amountPaise)}
            </p>
            <p className="text-xs text-muted mt-0.5">amount</p>
          </div>
        </header>

        {/* Reversal banner */}
        {isReversed ? (
          <Alert
            intent="bad"
            title={
              m.reversesMovementId !== null
                ? "This is a reversal row."
                : "This voucher has been voided."
            }
            description={
              m.reversesMovementId !== null
                ? "This row corrects an earlier voucher (append-only per ADR-0012 §3). The original is linked below."
                : "A reversal row has been posted. The original stays in history as the audit trail. Linked below."
            }
          />
        ) : null}

        {/* Document card */}
        <Card>
          <CardBody>
            <h2 className="text-xs uppercase tracking-wider text-muted font-semibold mb-3">
              Document
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <DetailRow label="Kind" value={prettyKind(m.kind)} />
              {m.kind === "petty_cash_expense" ? (
                <DetailRow label="Category" value={catMeta?.label ?? prettyCategory(category)} />
              ) : null}
              {m.kind === "bank_charge" && m.bankChargeSubtype ? (
                <DetailRow label="Subtype" value={prettyBankCharge(m.bankChargeSubtype)} />
              ) : null}
              <DetailRow label="Amount" value={paiseToInr(m.amountPaise)} mono />
              <DetailRow label="Value date" value={formatDate(m.valueDate)} />
              <DetailRow label="Counterparty" value={m.counterpartyLabel ?? "—"} />
              <DetailRow
                label="Posted at"
                value={new Date(m.createdAt).toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              />
              <div className="sm:col-span-2">
                <DetailRow label="Narration" value={m.narration} multiline />
              </div>
            </dl>
          </CardBody>
        </Card>

        {/* Linked records */}
        <Card>
          <CardBody>
            <h2 className="text-xs uppercase tracking-wider text-muted font-semibold mb-3">
              Linked records
            </h2>
            <ul className="flex flex-col gap-2 text-sm">
              {m.fromAccountId ? (
                <li>
                  <Link
                    to="/cash/bank-accounts/$bankAccountId"
                    params={{ bankAccountId: m.fromAccountId }}
                    className="inline-flex items-center gap-2 text-sky-700 hover:underline"
                  >
                    <Wallet size={14} strokeWidth={2} aria-hidden />
                    Source · {m.fromAccountName ?? m.fromAccountId}
                  </Link>
                </li>
              ) : null}
              {m.toAccountId ? (
                <li>
                  <Link
                    to="/cash/bank-accounts/$bankAccountId"
                    params={{ bankAccountId: m.toAccountId }}
                    className="inline-flex items-center gap-2 text-sky-700 hover:underline"
                  >
                    <Wallet size={14} strokeWidth={2} aria-hidden />
                    Destination · {m.toAccountName ?? m.toAccountId}
                  </Link>
                </li>
              ) : null}
              {m.reversedByMovementId ? (
                <li>
                  <Link
                    to="/cash/petty-cash/$voucherId"
                    params={{ voucherId: m.reversedByMovementId }}
                    className="inline-flex items-center gap-2 text-sky-700 hover:underline"
                  >
                    <RotateCcw size={14} strokeWidth={2} aria-hidden />
                    Reversed by · {m.reversedByMovementId.slice(0, 8)}…
                  </Link>
                </li>
              ) : null}
              {m.reversesMovementId ? (
                <li>
                  <Link
                    to="/cash/petty-cash/$voucherId"
                    params={{ voucherId: m.reversesMovementId }}
                    className="inline-flex items-center gap-2 text-sky-700 hover:underline"
                  >
                    <RotateCcw size={14} strokeWidth={2} aria-hidden />
                    Reverses · {m.reversesMovementId.slice(0, 8)}…
                  </Link>
                </li>
              ) : null}
            </ul>
          </CardBody>
        </Card>

        {/* Action row */}
        {!isReversed ? (
          <div className="flex items-center justify-end gap-2">
            <Button intent="danger" onClick={() => setVoidOpen(true)}>
              Void this voucher
            </Button>
          </div>
        ) : null}

        {reverseMut.isSuccess ? (
          <Alert
            intent="info"
            title="Voided."
            description="The reversal posts as a new append-only row per ADR-0012 §3."
          />
        ) : null}
      </div>

      <VoidMovementDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title="Void this voucher?"
        amountLabel={`−${paiseToInr(m.amountPaise)}`}
        kindLabel={prettyKind(m.kind)}
        pending={reverseMut.isPending}
        {...(reverseMut.error ? { errorMessage: reverseMut.error.message } : {})}
        onConfirm={(reason) => reverseMut.mutate({ movementId: voucherId, reason })}
      />
    </WorkspaceShell>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function KindPill({ kind }: { kind: string }) {
  return (
    <span className="inline-flex items-center px-1.5 h-5 rounded-full border border-line bg-raised text-muted text-xs font-semibold uppercase tracking-wider">
      {prettyKind(kind)}
    </span>
  );
}

function DetailRow({
  label,
  value,
  mono,
  multiline,
}: {
  label: string;
  value: string;
  mono?: boolean;
  multiline?: boolean;
}) {
  return (
    <div
      className={multiline ? "flex flex-col gap-1" : "flex items-baseline justify-between gap-3"}
    >
      <dt className="text-muted text-xs">{label}</dt>
      <dd
        className={
          "text-sm text-ink-2 font-medium " +
          (mono ? "num tabular-nums " : "") +
          (multiline ? "whitespace-pre-wrap" : "text-right")
        }
      >
        {value}
      </dd>
    </div>
  );
}

// ── Label helpers ───────────────────────────────────────────────────

const KIND_LABEL: Record<string, string> = {
  petty_cash_expense: "Petty cash voucher",
  transfer: "Transfer",
  refund_customer: "Customer refund",
  statutory_payment: "Statutory payment",
  owner_contribution: "Owner contribution",
  owner_drawing: "Owner drawing",
  loan_disbursement: "Loan disbursement",
  loan_repayment: "Loan repayment",
  interest_paid: "Interest paid",
  stock_writeoff: "Stock write-off",
  cash_count_adjustment: "Cash count adjustment",
  deposit_paid: "Deposit paid",
  deposit_received: "Deposit received",
  bank_charge: "Bank charge",
};

function prettyKind(kind: string): string {
  return KIND_LABEL[kind] ?? kind.replaceAll("_", " ");
}

function prettyCategory(slug: PettyCashCategory): string {
  switch (slug) {
    case "postage":
      return "Postage";
    case "conveyance":
      return "Conveyance";
    case "travel":
      return "Travel";
    case "refreshments":
      return "Refreshments";
    case "office_supplies":
      return "Office supplies";
    case "printing_stationery":
      return "Printing";
    case "repairs_maintenance":
      return "Repairs";
    case "sundries":
      return "Sundries";
  }
}

const BANK_CHARGE_LABEL: Record<string, string> = {
  fx_markup: "FX markup",
  card_annual_fee: "Card annual fee",
  late_fee: "Late fee",
  wire_transfer_fee: "Wire transfer fee",
  atm_fee: "ATM fee",
  account_maintenance: "Account maintenance",
  other: "Other",
};

function prettyBankCharge(subtype: string): string {
  return BANK_CHARGE_LABEL[subtype] ?? subtype.replaceAll("_", " ");
}
